import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SchemaForm from "../SchemaForm";
import TestAction from "../TestAction";
import { getButtonText } from "../TestActionFunctions";
import { ConfigTarget, configFor, isBlank, missingRequiredFields } from "./actionSetup";

export type TestActionProps = {
    onTestAction: (actionId: any, actionValue: any) => void;
    loading?: boolean;
    response?: any;
};

type ActionSectionFormProps = {
    target: ConfigTarget;
    action: any;
    formData: any;
    formVersion?: number;
    errors?: Record<string, string>;
    editable?: boolean;
    userIsAdmin?: boolean;
    token?: string;
    onChange: (data: any, fieldId?: string) => void;
    test?: TestActionProps;
    // Create flows have nothing saved yet by definition, so the never-saved
    // banner and the required-field hints would state the obvious on every field.
    showSetupState?: boolean;
    // Replaces the Test Action row — create flows show it disabled with a
    // "save first" tooltip instead of a live button.
    headerActions?: React.ReactNode;
};

// One action's configuration form: the section body every configuration screen
// renders. Owns the two "this isn't live yet" signals — the never-saved banner and
// the per-field required hints — so they read identically everywhere.
const ActionSectionForm = ({
    target, action, formData, formVersion = 0, errors, editable = true, userIsAdmin = false, token, onChange, test,
    showSetupState = true, headerActions,
}: ActionSectionFormProps) => {
    const { t } = useTranslation();
    const [tested, setTested] = useState(false);
    const saved = configFor(target, action);

    // Sections swap this component's props rather than remounting it, so the
    // "a test ran" flag has to follow the action or the previous action's response
    // shows up under the next one.
    useEffect(() => { setTested(false); }, [action.id]);

    // Mark required fields that have no value the same way validation errors
    // render — that's the "what's missing" pointer the "!" badge promises. A field
    // showing a schema default is left alone: the banner above already says none of
    // these values are saved, and repeating it per field reads as a contradiction
    // next to a populated input.
    const properties = action.schema?.properties ?? {};
    const missing = showSetupState
        ? missingRequiredFields(target, action).filter((f) => isBlank(properties[f]?.default))
        : [];
    let setupHints: Record<string, string> | undefined;
    if (missing.length > 0) {
        setupHints = {};
        missing.forEach((f) => { setupHints![f] = t("Required — not configured yet."); });
    }

    return (
        <div className="rounded border border-field-outline bg-white p-4">
            {showSetupState && !saved && (
                <div className="mb-3 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black">
                    {t("This action hasn't been saved yet. The values below are defaults and won't apply until you save.")}
                </div>
            )}
            {/* Same corner as the live Test Connection button, so a create form and
                an existing account's form have the same layout. */}
            {headerActions && <div className="mb-3 flex justify-end">{headerActions}</div>}
            {action.schema?.is_executable === true && test && (
                <div className="mb-3">
                    <TestAction
                        actionId={action.id}
                        actionValue={action.value}
                        onTestAction={(id: any, value: any) => { test.onTestAction(id, value); setTested(true); }}
                        isLoading={!!test.loading && tested}
                        response={tested ? test.response : null}
                        buttonText={getButtonText(action.value, t)}
                        formTitle=""
                        uiSchema={action.ui_schema || {}}
                    />
                </div>
            )}
            <SchemaForm
                key={`${action.id}-v${formVersion}`}
                userIsAdmin={userIsAdmin}
                formTitle=""
                schema={action.schema}
                ui_schema={action.ui_schema ? { ...action.ui_schema, "ui:description": action.ui_schema["ui:description"] || "" } : {}}
                formdata={formData}
                onChange={(e: any, fieldId?: string) => onChange(e, fieldId)}
                isEditable={editable}
                errors={errors ?? setupHints}
                integrationId={target.id}
                authToken={token}
            />
        </div>
    );
};

export default ActionSectionForm;
